import {
  Article,
  ArticleAction,
  ArticleImage,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Button } from "@babackapp/customer-ui/components/button";
import { Badge } from "@babackapp/ui/components/badge";
import { getLineItemDisplay } from "@babackapp/return-widget-components/utils/line-item-display";
import type { DraftLineItem, Order, OrderLineItem } from "@babackapp/return-widget-components/types";
import { Trash2Icon } from "lucide-react";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";

type VariantOption = { name: string; value: string };
type Variant = { id: string; selectedOptions: VariantOption[] };
type Product = { variants: Variant[] };

function getExchangeVariantLabel(order: Order | null, lineItemId: string, exchangeVariantId: string): string | null {
  const collection = (order as unknown as Record<string, unknown>)?.variantByLineItemId as Record<string, { products: Product[] }> | undefined;
  const products = collection?.[lineItemId]?.products ?? [];
  for (const product of products) {
    const variant = product.variants.find((v) => v.id === exchangeVariantId);
    if (variant) {
      return variant.selectedOptions
        .map((o) => o.value)
        .filter((v) => v !== "Default Title")
        .join(" / ") || null;
    }
  }
  return null;
}

type Props = {
  item: DraftLineItem;
  orderLineItems: OrderLineItem[];
  order: Order | null;
  isRetractation?: boolean;
  onRemove: () => void;
};

export function SummaryLineItem({ item, orderLineItems, order, isRetractation, onRemove }: Props) {
  const t = useCustomText();
  const { title, subtitle, image } = getLineItemDisplay(orderLineItems, item.lineItemId);

  const exchangeLabel =
    item.resolution === "exchange" && item.exchangeVariantId
      ? getExchangeVariantLabel(order, item.lineItemId, item.exchangeVariantId)
      : null;

  const variantDisplay = exchangeLabel && subtitle
    ? `${subtitle} → ${exchangeLabel}`
    : exchangeLabel ?? subtitle;

  return (
    <Article>
      <ArticleImage src={image ?? undefined} layoutId={`img-${item.lineItemId}`} />
      <ArticleTitle className="flex items-center gap-1.5">
        {title}
        {isRetractation && <Badge variant="secondary" size="xs">{t("returnMethod.retractation.label")}</Badge>}
      </ArticleTitle>
      {variantDisplay ? (
        <ArticleSubtitle>{variantDisplay} — ×{item.quantity}</ArticleSubtitle>
      ) : (
        <ArticleSubtitle>×{item.quantity}</ArticleSubtitle>
      )}
      <ArticleAction autoHide={false}>
        <Button variant="outline" size="icon-sm" onClick={onRemove}>
          <Trash2Icon />
        </Button>
      </ArticleAction>
    </Article>
  );
}
